/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { DataMap } from '@grapecity/wijmo.grid';
import { useMessageBar } from 'hooks/useMessageBar';
import { ApprovalAppd, SaveApprover } from 'models/approves/ReferenceOpinion';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { Code, commonYNList } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { getExcpAuthGrantorList } from 'apis/admin/ExcpRole';
import UserModal from 'components/modals/common/UserModal';
import { Employee } from 'models/admin/Employee';
import { CellRangeEventArgs } from '@grapecity/wijmo.grid';
import { commonYNarr, commonYNcodes } from '../../models/common/Common';
import { CrudCode } from '../../models/common/Edit';
import useEvent from 'react-use-event-hook';
import { ApproveRequestPageType } from '../approves/ApproveRequestDetailCase';
import DOMPurify from 'dompurify';

type PropType = {
  approvalAppd: SaveApprover[];
  approvalInfr: SaveApprover[];
  onApprovalsChange?: (list: SaveApprover[]) => void;
  onNotifiersChange?: (list: SaveApprover[]) => void;
  pageId?: any;
  approvalPrj?: any;
  approvalRole?: any;
};

/**
 * @desc 결재 라인 등록/수정 (승인자/통보자 정보)
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.31   jshwang12       셋업일정관련 요청인 경우 결재자 지정 불가, 통보자 미노출
 * 2025.11.07   shkim0407       협력사 권한 요청의 경우 결재자 셋팅 추가
 * 2025.11.23   shkim0407       협력사 권한 요청의 경우 벨리데이션 추가
 * 2026.02.02   jihwan89        ELM 권한요청, 협력사 권한요청 문구추가 (총괄님요청)
 */
const ApprovalLineInfo = (props: PropType) => {
  const { approvalAppd, approvalInfr, onApprovalsChange, onNotifiersChange } = props;
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [approverManagementModalOpen0, setApproverManagementModalOpen0] = useState(false);
  const [approverManagementModalOpenC, setApproverManagementModalOpenC] = useState(false);
  const [rowDataAppd, setRowDataAppd] = useState<SaveApprover[]>(approvalAppd);
  const [rowDataInfr, setRowDataInfr] = useState<SaveApprover[]>(approvalInfr);
  const [aprTpDivsList, setAprTpDivsList] = useState<Code[]>();
  const isSetupRequest = useMemo(
    () =>
      [
        ApproveRequestPageType.SETUP_SCHD_REG,
        ApproveRequestPageType.SETUP_SCHD_CHG,
        ApproveRequestPageType.EXCP_AUTH_REQ,
      ].includes(props.pageId),
    [props.pageId]
  );

  useEffect(() => {
    if (!gridRef.current) return;
    const cv = gridRef.current.collectionView;
    cv.sourceCollection = []; // 데이터 비우기
    onApprovalsChange?.([]);
  }, [props.approvalPrj]);

  useEffect(() => {
    if (!gridRef.current) return;
    const cv = gridRef.current.collectionView;
    cv.sourceCollection = []; // 데이터 비우기
    onApprovalsChange?.([]);
  }, [props.approvalRole]);

  /* [25.11.23] 협력사 권한 요청의 경우 벨리데이션 */
  const validation = () => {
    const prjList = props.approvalPrj && props.approvalPrj.length > 0 ? props.approvalPrj : [];
    const roleList = props.approvalRole && props.approvalRole.length > 0 ? props.approvalRole : [];

    if (roleList.length == 0) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.권한은 최소 하나 이상 선택 되어야 합니다.',
          '권한은 최소 하나 이상 선택 되어야 합니다.'
        ),
      });
      return false;
    }

    const hasPrjUseYn = props.approvalRole.some((item) => item.prjUseYn === 'Y');
    if (hasPrjUseYn && prjList.length == 0) {
      openMessageBar({
        type: 'error',
        content: t(
          'com.msg.프로젝트는 최소 하나 이상 선택 되어야 합니다.',
          '프로젝트는 최소 하나 이상 선택 되어야 합니다.'
        ),
      });
      return false;
    }
    return true;
  };

  /* [25.11.07] 협력사 권한 요청 관련 결재자 셋팅 부분 */
  const handleGrantorListSet = async () => {
    if (!(await validation())) return;

    const prjList = props.approvalPrj && props.approvalPrj.length > 0 ? props.approvalPrj : [];
    (async () => {
      const result = await getExcpAuthGrantorList(prjList);
      if (result) {
        const saveApprover = ((result || []) as Employee[]).map((o) => {
          return {
            ...o,
            status: '0', // 결재
            aprAprvUserId: o.userId,
            aprTpDivsCd: '0',
            grnrAprTpDivsCd: '0', // 결재 0 , 합의 6 - 결재 고정이지만 병렬 Y로 선택할경우 합의 6으로 변경됨.(서비스에서변경)
            prlYn: 'Y', // 병렬여부
          } as SaveApprover;
        });
        setRowDataAppd(saveApprover);
        onApprovalsChange?.(saveApprover);
      }
    })();
  };

  useEffect(() => {
    getCommonCodesForGrid();
  }, []);

  useEffect(() => {
    setRowDataAppd(approvalAppd);
    setRowDataInfr(approvalInfr);
  }, [approvalAppd, approvalInfr]);

  const getCommonCodesForGrid = async () => {
    setAprTpDivsList((await getCommonCodeNames('APR_TP_DIVS_CD')) ?? []);
  };

  const gridRef = useRef<any>();
  const dragIndex = useRef<any>();
  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.formatItem.addHandler((s, e) => {
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          e.cell.draggable = true;
        }
      }
    });

    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));

      grid.invalidate();
    });
  };

  const onCellEditEnded = useEvent((grid, e) => {
    // 바운딩된 컬럼명
    const col = grid.columns[e.col];
    const item = grid.collectionView.currentItem;

    if (col.binding === 'prlYn') {
      //병렬 Y 면 구분 = 병렬합의
      if (item.prlYn === 'Y') {
        item.grnrAprTpDivsCd = '10';
      } else {
        item.grnrAprTpDivsCd = '0';
      }
      grid.collectionView.commitEdit();
    }
  });

  const beginningEdit = useEvent((s, e) => {
    const item = s.rows[e.row].dataItem;
    const col = s.columns[e.col];
    const rowIndex = e.row;

    // if ('prlYn' === col.binding && e.row === 0) {
    //   s.setCellData(rowIndex, 'grnrAprTpDivsCd', '0'); //ss
    //   s.setCellData(rowIndex, col.binding, 'N'); //ss
    //   e.cancel = true;
    // }
  });

  const layoutDefinitionAppd = useMemo(
    () => [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'sortable',
        header: String(t('com.label.순서변경', '순서변경')),
        width: 80,
        cssClass: 'WijmoCellMove',
        cellTemplate: '<Button />',
        isReadOnly: true,
        visible: !isSetupRequest,
      },
      {
        binding: 'grnrAprTpDivsCd',
        header: `${t('com.grid.구분', '구분')}`,
        align: 'center',
        width: 100,
        cssClass: 'WijmoSelect',
        isReadOnly: true,
        dataMap: new DataMap(aprTpDivsList || [], 'cmnCd', 'cmnCdNm'),
        visible: !isSetupRequest,
      },
      {
        binding: 'prlYn',
        header: `${t('com.grid.병렬', '병렬')}`,
        align: 'center',
        width: 60,
        dataMap: new DataMap(commonYNcodes || [], 'cmnCd', 'cmnCdNm'),
        visible: !isSetupRequest,
      },
      {
        binding: 'empNm',
        header: `${t('com.grid.성명', '성명')}`,
        align: 'center',
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'jtiNm',
        header: `${t('com.grid.직책/직급호칭', '직책/직급호칭')}`,
        align: 'center',
        width: 150,
        isReadOnly: true,
      },
      {
        binding: 'deptNm',
        header: `${t('com.grid.부서', '부서')}`,
        width: 200,
        align: 'left',
        isReadOnly: true,
      },
      {
        header: `${t('com.grid.결재시간', '결재시간')}`,
        align: 'center',
        width: 180,
        isReadOnly: true,
      },
      {
        header: `${t('com.grid.결재결과', '결재결과')}`,
        align: 'center',
        width: 120,
        isReadOnly: true,
      },
      {
        header: `${t('com.grid.결재의견', '결재의견')}`,
        align: 'left',
        width: '*',
        isReadOnly: true,
      },
    ],
    [aprTpDivsList, isSetupRequest]
  );

  const layoutDefinitionInfr = [
    {
      binding: 'status',
      header: `${t('com.grid.구분', '구분')}`,
      align: 'center',
      width: 100,
      cellTemplate: (params) => {
        return `${t('com.label.통보', '통보')}`;
      },
    },
    {
      binding: 'aprvUserNm',
      header: `${t('com.grid.성명', '성명')}`,
      align: 'center',
      width: 200,
    },
    {
      binding: 'aprvUserJikwiNm',
      header: `${t('com.grid.직책/직급호칭', '직책/직급호칭')}`,
      align: 'center',
      width: 200,
    },
    {
      binding: 'aprvUserDeptNm',
      header: `${t('com.grid.부서', '부서')}`,
      width: '*',
      align: 'left',
    },
  ];

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('com.label.승인자 정보', '승인자 정보')}</h3>
          {(props.pageId == 'UT_LIB_REQ' ||
            props.pageId == 'UT_LIB_UPDATE_REQ' ||
            props.pageId == 'UT_LIB_DELETE_REQ') && (
            <div className="info warning">
              {t(
                'com.msg.유틸리티 기술팀 담당자는 필수로 지정 해야 합니다.',
                '유틸리티 기술팀 담당자는 필수로 지정 해야 합니다.'
              )}
            </div>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          {['EXCP_AUTH_REQ', 'ELM_AUTH_REQ'].includes(props.pageId) && (
            <SubTitleGroup>
              <div
                className="total"
                style={{ alignContent: 'center', paddingRight: '10px' }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    '※ 요청하는 권한에 맞는 결재자를 1명만 선택해 주세요.'
                  ),
                }}
              ></div>
            </SubTitleGroup>
          )}
          {/* [25.10.31] 셋업일정관련 요청인 경우 결재자 지정 불가, 통보자 미노출 */}
          {!isSetupRequest && (
            <Button
              css={IconButton.button}
              className="check"
              onClick={() => setApproverManagementModalOpen0(true)}
            >
              {t('com.button.결재자 지정', '결재자 지정')}
            </Button>
          )}
          {/* [25.11.07] 협력사 권한 요청 관련 결재자 셋팅 부분 */}
          {isSetupRequest && props.pageId == 'EXCP_AUTH_REQ' && (
            <Button css={IconButton.button} className="check" onClick={handleGrantorListSet}>
              {t('com.button.결재자 지정', '결재자 지정')}
            </Button>
          )}
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'ApproveLinInfoAppdGridId'}
        layoutDefinition={layoutDefinitionAppd}
        rowData={rowDataAppd}
        initialized={onInitialized}
        beginningEdit={beginningEdit}
        cellEditEnded={onCellEditEnded}
        height={250}
        isReadOnly={false}
        isFilter={false}
        isSelector={false}
        allowPinning={false}
        draggingRow={(grid, e) => {
          dragIndex.current = e.row;
          grid.collectionView.moveCurrentToPosition(e.row);
        }}
        draggedRow={(grid, e) => {
          const dropIndex = e.row;
          const arr = grid.collectionView.sourceCollection;
          grid.collectionView.deferUpdate(() => {
            const item = arr[dragIndex.current];
            arr.splice(dragIndex.current, 1);
            arr.splice(dropIndex, 0, item);
            grid.collectionView.moveCurrentToPosition(dropIndex);
            // grid.collectionView.items.forEach((item, i) => {
            //   if (i === 0) {
            //     item.grnrAprTpDivsCd = '0';
            //     item.prlYn = 'N';
            //   }
            // });
          });
        }}
      />
      {/* [25.10.31] 셋업일정관련 요청인 경우 결재자 지정 불가, 통보자 미노출 */}
      {!isSetupRequest && (
        <>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.통보자 정보', '통보자 정보')}</h3>
              {props.pageId === ApproveRequestPageType.IDLE_ASST_REQ ? (
                <div className="info primary">
                  {t(
                    'asst.label.시스템 내 등록된 법인별 설비담당자들에게는 자동으로 통보됩니다.',
                    '시스템 내 등록된 법인별 설비담당자들에게는 자동으로 통보됩니다.'
                  )}
                </div>
              ) : (
                ''
              )}
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="check"
                onClick={() => setApproverManagementModalOpenC(true)}
              >
                {t('com.button.통보자 지정', '통보자 지정')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            layoutDefinition={layoutDefinitionInfr}
            rowData={rowDataInfr}
            height={250}
            isReadOnly={true}
            isFilter={false}
            isSelector={false}
            allowPinning={false}
          />
        </>
      )}

      {approverManagementModalOpen0 && (
        <UserModal
          open={approverManagementModalOpen0}
          close={() => setApproverManagementModalOpen0(false)}
          title={t('com.label.결재자 지정', '결재자 지정')}
          defaultUserId={(rowDataAppd || [])
            .map((o) => o.userId)
            .filter((o) => !!o)
            .join(',')}
          singleSelect={false}
          sortable={true}
          condition={{
            // [25.12.19] ELM 권한요청인 경우 ADM_PI 권한 사용자만 지정가능
            roles: props?.pageId === ApproveRequestPageType.ELM_AUTH_REQ ? ['ADM_PI'] : [],
          }}
          onCallback={(result) => {
            // '0' : 결재
            const saveApprover = ((result || []) as Employee[]).map((o) => {
              console.log(result);
              return {
                ...o,
                status: '0', // 결재
                aprAprvUserId: o.userId,
                aprTpDivsCd: '0',
                grnrAprTpDivsCd: '0', // 결재 0 , 합의 6 - 결재 고정이지만 병렬 Y로 선택할경우 합의 6으로 변경됨.(서비스에서변경)
                prlYn: 'N', // 병렬여부
              } as SaveApprover;
            });
            setRowDataAppd(saveApprover);
            onApprovalsChange?.(saveApprover);
          }}
        />
      )}
      {approverManagementModalOpenC && (
        <UserModal
          open={approverManagementModalOpenC}
          close={() => setApproverManagementModalOpenC(false)}
          title={t('com.label.통보자 지정', '통보자 지정')}
          defaultUserId={(rowDataInfr || [])
            .map((o) => o.userId)
            .filter((o) => !!o)
            .join(',')}
          singleSelect={false}
          sortable={true}
          onCallback={(result) => {
            const saveApprover = ((result || []) as Employee[]).map((o) => {
              return {
                ...o,
                status: 'C', // 통보
                aprAprvUserId: o.userId,
                grnrAprTpDivsCd: 'C', // 통보
                aprvUserNm: o.empNm,
                aprvUserJikwiNm: o.jtiNm,
                aprvUserDeptNm: o.deptNm,
                deptNm: o.deptNm,
                empNm: o.empNm,
                jtiNm: o.jtiNm,
              } as SaveApprover;
            });
            setRowDataInfr(saveApprover);
            onNotifiersChange?.(saveApprover);
          }}
        />
      )}
    </>
  );
};

export default React.memo(ApprovalLineInfo);
